import { useQuery } from '@tanstack/react-query';
import { HeroPdpPageQueryKeys } from '@enums/react-query-keys';
import mapCatalogHitToProduct, { mapSimilarProductToProduct } from '@helpers/utils/catalog/map-catalog-hit-to-product';
import { CatalogProduct } from '@interfaces/models/catalogProduct';
import { Preferences } from '@interfaces/models/preferences';
import { ElasticSearch } from '@interfaces/models/elasticSearch';
import SearchService from '@services/search-service';
import { mapCountryToTheSizeType, parseURLHashes } from '@helpers/utils/catalog/catalog-url-helpers';
import logger from '@helpers/utils/logger/client';
import ProductService from '@services/product-service';
import { ServerAxiosRequestData } from '@config/axios';

export interface HeroPdpSimilarProductQuery {
  similarProducts: CatalogProduct[];
  showMoreVisible: boolean;
  productFeedCategory: 'hero_pdp_recommend' | 'hero_pdp_search';
}
export const getHeroPdpSimilarProductsQuery = async ({
  url,
  locale,
  productId,
  serverAxiosRequestData,
  limit = 36,
}: {
  url: string;
  locale: Pick<Preferences, 'country' | 'currency' | 'language' | 'regionName'>;
  productId: string | undefined;
  serverAxiosRequestData: ServerAxiosRequestData;
  limit?: number;
}): Promise<HeroPdpSimilarProductQuery> => {
  if (!productId) {
    return { similarProducts: [], showMoreVisible: false, productFeedCategory: 'hero_pdp_search' };
  }
  const similarItems = await ProductService.listSimilarProducts({
    productId,
    limit,
    disableSizeFilter: true,
    serverAxiosRequestData,
  });

  // in the rare case that we have less than 12 items, we fall back to the search service
  if (similarItems.length >= 12) {
    const catalogProducts = similarItems.map((product) => mapSimilarProductToProduct(product));
    return { similarProducts: catalogProducts, showMoreVisible: true, productFeedCategory: 'hero_pdp_recommend' };
  } else {
    logger.info(
      `HeroPDP productID:${productId} - Not enough similar items (${similarItems.length}) from the recommendation service, falling back to search service`,
    );
  }

  const filters = parseURLHashes(url)?.filters ?? null;
  const searchPayload: Partial<ElasticSearch> = {
    sortBy: 'relevance',
    pagination: { offset: 0, limit: 36 },
    locale: {
      country: locale.country,
      currency: locale.currency,
      language: locale.language,
      sizeType: mapCountryToTheSizeType(locale.regionName),
    },
    filters,
    mySizes: null,
  };

  // if there are not enough similar products from the recommendation service, we get the products from the search service
  const searchResponse = await SearchService.productSearch(searchPayload);
  const catalogProducts = (searchResponse?.items ?? []).map((product) =>
    mapCatalogHitToProduct(product, locale.country, locale.currency, locale.language),
  );
  const showMoreVisible = catalogProducts.length > 0 && searchResponse?.paginationStats?.totalPages > 1;
  return { similarProducts: catalogProducts, showMoreVisible, productFeedCategory: 'hero_pdp_search' };
};

const useHeroPdpSimilarProductsQuery = () => {
  return useQuery<HeroPdpSimilarProductQuery>({
    queryKey: [HeroPdpPageQueryKeys.GET_SIMILAR_PRODUCTS],
    staleTime: Infinity,
  });
};

export default useHeroPdpSimilarProductsQuery;
